/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Radio,
  RadioGroup,
  Select,
  StackItem,
  Text,
  VStack,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Api } from 'api';
import { DateRangePreSelector, ExpertisePointSelect, OccopayDrawer, LocalizedRangeDatepicker } from 'components';
import { FormControlFormInput, GenericMessages, OnlyNumberRegex, usePageTitle } from 'core';
import { useFormik } from 'formik';
import { useState, useEffect } from 'react';
import { useMutation, useQuery } from 'react-query';
import {
  ExpertisePoint,
  ExpertisePointQueryCacheKey,
  ExpertisePointGroupTypeLabel,
  PaymentTransationStartProcessData,
  TotalBalanceSheetData,
} from 'types';

import * as yup from 'yup';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { t, Trans } from '@lingui/macro';
import { AdminExpertisePointBalanceHistoryTable, AdminPageTitle } from '../components';

const validationSchema = yup.object({
  amount: yup.string().when('paymentMethod', {
    is: 'creditCard',
    then: (schema) =>
      schema
        .required(GenericMessages.CannotBeEmpty)
        .matches(OnlyNumberRegex, GenericMessages.OnlyNumber)
        .test(
          'less than 2000',
          GenericMessages.CantBeLessThanNumber(2000),
          (value) => !value || parseInt(value, 10) >= 2000,
        ),
    otherwise: (schema) => schema.notRequired(),
  }),
  paymentMethod: yup.string().required(GenericMessages.CannotBeEmpty),
});

export const AdminBalancePage = () => {
  const toast = useToast();
  const authUserInfo = useSelector((state: RootState) => state.auth.authUserInfo);
  const [expertisePointId, setExpertisePointId] = useState<number>();

  // Set page title
  usePageTitle('Bakiye Yönetimi');
  const [selectedBrand, setSelectedBrand] = useState<number>(() => {
    const saved = localStorage.getItem('selectedExpertisePointGroupType');
    return saved ? parseInt(saved, 10) : 0;
  });
  const [occopayPaymentData, setOccopayPaymentData] = useState<PaymentTransationStartProcessData>();
  const { isOpen: isOccopayDrawerOpen, onClose: onOccopayDrawerClose, onOpen: onOccopayDrawerOpen } = useDisclosure();
  const [selectedDates, setSelectedDates] = useState<Date[]>([
    new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // 7 gün öncesi
    new Date(),
  ]);

  useEffect(() => {
    const handleExpertisePointGroupTypeChange = (event: any) => {
      const { value } = event.detail;
      setSelectedBrand(value ? parseInt(value, 10) : 0);
    };

    window.addEventListener('expertisePointGroupTypeChanged', handleExpertisePointGroupTypeChange);
    return () => window.removeEventListener('expertisePointGroupTypeChanged', handleExpertisePointGroupTypeChange);
  }, []);

  // Dark mode compatible colors
  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorderColor = useColorModeValue('gray.200', 'gray.600');
  const headingColor = useColorModeValue('gray.800', 'white');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const accentColor = useColorModeValue('blue.600', 'blue.300');

  // Bank transfer explanation card colors
  const bankCardBg = useColorModeValue('blue.50', 'blue.900');
  const bankCardBorderColor = useColorModeValue('blue.200', 'blue.600');
  const bankCardHeadingColor = useColorModeValue('blue.700', 'blue.200');
  const bankCardTextColor = useColorModeValue('gray.700', 'gray.300');
  const bankDetailsCardBg = useColorModeValue('white', 'gray.700');
  const bankDetailsCardBorderColor = useColorModeValue('blue.300', 'blue.500');
  const bankDetailsTextColor = useColorModeValue('blue.700', 'blue.200');

  // Form colors
  const inputBg = useColorModeValue('white', 'gray.700');
  const inputBorderColor = useColorModeValue('gray.300', 'gray.600');
  const radioColor = useColorModeValue('blue.600', 'blue.300');

  const { data: transactionData, refetch: refetchBalances } = useQuery(
    [
      ExpertisePointQueryCacheKey.GetPreviousBalanceTransactions,
      expertisePointId,
      selectedDates[0],
      selectedDates[1],
      selectedBrand,
    ],
    async () => {
      const response = await Api.ExpertisePoint.getTransactions(
        expertisePointId,
        selectedDates[0].getTime(),
        selectedDates[1].getTime(),
        selectedBrand === 0 ? undefined : selectedBrand,
      );
      const responseData = response.data;
      const convertedTransactionData: TotalBalanceSheetData = {
        totalBalance: responseData.totalBalance,
        transactions: responseData.transactions.map((transaction) => {
          return {
            id: transaction.id,
            amount: transaction.amount,
            isCompleted: transaction.isCompleted,
            transactionDate: transaction.transactionDate ? new Date(transaction.transactionDate) : undefined,
            expertisePointCityName: transaction.expertisePointCityName,
            expertisePointCountyName: transaction.expertisePointCountyName,
            expertisePointName: transaction.expertisePointName,
            transactionSource: transaction.transactionSource,
            groupType: transaction.groupType,
            balanceAfterTransaction: transaction.balanceAfterTransaction,
          };
        }),
      };
      return convertedTransactionData;
    },
    {
      refetchOnWindowFocus: false,
    },
  );
  const paymentStartMutation = useMutation<PaymentTransationStartProcessData, any, number>(
    async (amount: number) => {
      const response = await Api.ExpertisePoint.startProcess(amount);
      return { ...response.data, amount };
    },
    {
      onSuccess: (data: PaymentTransationStartProcessData) => {
        setOccopayPaymentData(data);
        onOccopayDrawerOpen();
        // eslint-disable-next-line no-use-before-define
        resetForm();
      },
      onError: (err) => {
        console.error(err);
        toast({
          title: 'Ödeme sistemi ile haberleşirken hata oluştu',
          status: 'error',
          isClosable: true,
        });
      },
    },
  );

  const { values, handleChange, handleSubmit, errors, resetForm, setFieldValue } = useFormik({
    initialValues: {
      amount: '',
      paymentMethod: 'creditCard',
    },
    validationSchema,
    onSubmit: (data: { amount: string; paymentMethod: string }) => {
      if (data.paymentMethod === 'creditCard' && data.amount) {
        paymentStartMutation.mutate(parseInt(data.amount, 10));
      }
      // For bank transfers, we don't need to process payment, just show instructions
    },
  });

  return (
    <>
      <OccopayDrawer paymentData={occopayPaymentData} isOpen={isOccopayDrawerOpen} onClose={onOccopayDrawerClose} />
      <AdminPageTitle title={t`Bakiye Geçmişi`} />

      <VStack spacing={6} align="stretch">
        {/* Balance Loading Section */}
        <Flex direction={['column', 'column', 'row']} gap={6} align="flex-start">
          <Card
            width={['100%', '100%', '350px']}
            bg={cardBg}
            borderColor={cardBorderColor}
            borderWidth="1px"
            shadow="lg"
            transition="all 0.2s"
            _hover={{ shadow: 'xl' }}
            flexShrink={0}
          >
            <CardHeader pb={2}>
              <Heading size="md" color={headingColor}>
                <Trans>Bakiye Yükle</Trans>
              </Heading>
            </CardHeader>
            <CardBody pt={2}>
              <form id="occopayAmountForm" onSubmit={handleSubmit}>
                <VStack spacing={4} align="stretch">
                  {values.paymentMethod === 'creditCard' && (
                    <FormControl>
                      <FormLabel color={textColor} fontWeight="semibold">
                        {t`Miktar`}
                      </FormLabel>
                      <FormControlFormInput
                        type="number"
                        name="amount"
                        onChange={handleChange}
                        value={values.amount || ''}
                        error={errors.amount != null}
                        helperText={errors.amount}
                        placeholder="Minimum 2000 TL"
                        bg={inputBg}
                        borderColor={inputBorderColor}
                        _focus={{
                          borderColor: accentColor,
                          boxShadow: `0 0 0 1px ${accentColor}`,
                        }}
                      />
                    </FormControl>
                  )}

                  <FormControl>
                    <FormLabel color={textColor} fontWeight="semibold">
                      <Trans>Ödeme Şekli</Trans>
                    </FormLabel>
                    <RadioGroup
                      value={values.paymentMethod}
                      onChange={(value) => {
                        setFieldValue('paymentMethod', value);
                        if (value === 'bankTransfer') {
                          setFieldValue('amount', '');
                        }
                      }}
                    >
                      <HStack spacing={8}>
                        <Radio
                          value="creditCard"
                          colorScheme="blue"
                          size="lg"
                          borderColor={useColorModeValue('gray.400', 'gray.500')}
                        >
                          <Text color={textColor} fontWeight="medium">
                            <Trans>Kredi Kartı</Trans>
                          </Text>
                        </Radio>
                        <Radio
                          value="bankTransfer"
                          colorScheme="blue"
                          size="lg"
                          borderColor={useColorModeValue('gray.400', 'gray.500')}
                        >
                          <Text color={textColor} fontWeight="medium">
                            <Trans>Havale / EFT</Trans>
                          </Text>
                        </Radio>
                      </HStack>
                    </RadioGroup>
                  </FormControl>

                  {values.paymentMethod === 'creditCard' && (
                    <Button
                      type="submit"
                      form="occopayAmountForm"
                      colorScheme="blue"
                      size="md"
                      mt={2}
                      isLoading={paymentStartMutation.isLoading}
                      loadingText="İşleniyor..."
                      _hover={{
                        transform: 'translateY(-1px)',
                        shadow: 'md',
                      }}
                      transition="all 0.2s"
                    >
                      <Trans>Yükle</Trans>
                    </Button>
                  )}
                </VStack>
              </form>
            </CardBody>
          </Card>

          {/* Bank Transfer Information */}
          {values.paymentMethod === 'bankTransfer' && (
            <Card
              flex="1"
              maxWidth={['100%', '100%', '100%']}
              bg={bankCardBg}
              borderColor={bankCardBorderColor}
              borderWidth="2px"
              shadow="lg"
              transition="all 0.3s ease-in-out"
            >
              <CardHeader pb={2}>
                <Heading size="md" color={bankCardHeadingColor} textAlign="center">
                  <Trans>HAVALE/EFT İLE BAKİYE YÜKLEME</Trans>
                </Heading>
              </CardHeader>
              <CardBody pt={2}>
                <VStack spacing={4} align="stretch">
                  <Text
                    fontSize="sm"
                    textAlign="center"
                    color={useColorModeValue('red.600', 'red.300')}
                    lineHeight="1.6"
                    fontWeight="bold"
                  >
                    <Trans>
                      HAVALE SİSTEMDE KAYITLI VERGİ LEVHASİ ÜZERİNDEKİ İSİM TARAFINDAN YAPILMASI ZORUNLUDUR AKSİ HALDE
                      İADE EDİLECEKTİR.
                    </Trans>
                  </Text>

                  <VStack spacing={2}>
                    <Text
                      fontSize="sm"
                      textAlign="center"
                      color={useColorModeValue('orange.600', 'orange.300')}
                      fontWeight="bold"
                    >
                      <Trans>2000 TL altı havaleler sisteme işlenmemektedir.</Trans>
                    </Text>
                    <Text fontSize="sm" textAlign="center" color={bankCardTextColor} fontWeight="semibold">
                      <Trans>Lütfen minimum 2000 TL bakiye yüklemesi yapınız.</Trans>
                    </Text>
                    <Text
                      fontSize="sm"
                      textAlign="center"
                      color={useColorModeValue('green.600', 'green.300')}
                      fontWeight="semibold"
                    >
                      <Trans>Tutar hesaba geçtiği an otomatik olarak bakiye yüklenecektir.</Trans>
                    </Text>
                  </VStack>

                  {authUserInfo?.expertisePoint?.moneyTransferCode && (
                    <VStack
                      spacing={2}
                      p={4}
                      bg={useColorModeValue('yellow.50', 'yellow.900')}
                      borderRadius="lg"
                      border="2px solid"
                      borderColor={useColorModeValue('yellow.400', 'yellow.600')}
                    >
                      <Text
                        fontSize="lg"
                        fontWeight="bold"
                        color={useColorModeValue('yellow.800', 'yellow.200')}
                        textAlign="center"
                      >
                        <Trans>BAYİ EFT TRANSFER KODUNUZ</Trans>
                      </Text>
                      <Text
                        fontSize="2xl"
                        fontWeight="black"
                        color={useColorModeValue('yellow.900', 'yellow.100')}
                        textAlign="center"
                        fontFamily="mono"
                        letterSpacing="2px"
                      >
                        {authUserInfo?.expertisePoint?.moneyTransferCode}
                      </Text>
                      <Text
                        fontSize="sm"
                        fontWeight="bold"
                        color={useColorModeValue('yellow.800', 'yellow.200')}
                        textAlign="center"
                      >
                        <Trans>BU KODU AÇIKLAMAYA YAZINIZ</Trans>
                      </Text>
                    </VStack>
                  )}

                  <Text fontSize="sm" textAlign="center" color={bankCardTextColor} fontStyle="italic">
                    <Trans>Havale/EFT ile ödeme yapmanız durumunda aşağıdaki hesap numarasını kullanınız.</Trans>
                  </Text>

                  <VStack
                    spacing={3}
                    bg={bankDetailsCardBg}
                    p={4}
                    borderRadius="lg"
                    border="2px solid"
                    borderColor={bankDetailsCardBorderColor}
                    shadow="sm"
                  >
                    <Text fontWeight="bold" color={bankDetailsTextColor} fontSize="md">
                      <Trans>YAPI KREDİ ADANA ŞUBESİ</Trans>
                    </Text>
                    <Text
                      fontWeight="bold"
                      fontSize={['md', 'lg']}
                      color={bankDetailsTextColor}
                      fontFamily="mono"
                      letterSpacing="0.5px"
                      bg={useColorModeValue('gray.50', 'gray.800')}
                      px={2}
                      py={2}
                      borderRadius="md"
                      textAlign="center"
                      wordBreak="break-all"
                      lineHeight="1.2"
                    >
                      TR15 0006 7010 0000 0053 9943 93
                    </Text>
                    <Text fontWeight="bold" color={bankDetailsTextColor} fontSize="md" textAlign="center">
                      <Trans>TEKBAŞ TEKNOLOJİ OTOMOTİV A.Ş</Trans>
                    </Text>
                  </VStack>

                  <Text
                    fontSize="sm"
                    textAlign="center"
                    fontWeight="bold"
                    color={accentColor}
                    bg={useColorModeValue('blue.100', 'blue.800')}
                    px={3}
                    py={2}
                    borderRadius="md"
                  >
                    <Trans>MUHASEBE DESTEK : 0536 527 58 37</Trans>
                  </Text>
                </VStack>
              </CardBody>
            </Card>
          )}
        </Flex>

        {/* Date Range and Expertise Point Selection */}
        <HStack align="center" gap={10}>
          <StackItem>
            <DateRangePreSelector
              onDateRangeSelected={(startDate: Date, endDate: Date) => {
                setSelectedDates([startDate, endDate]);
              }}
            />
          </StackItem>
          {(authUserInfo?.isSuperAdmin || authUserInfo?.isAdmin) && (
            <>
              <StackItem w="200px">
                <FormControl>
                  <FormLabel>
                    <Trans>Marka</Trans>
                  </FormLabel>
                  <Select value={selectedBrand} onChange={(e) => setSelectedBrand(Number(e.target.value))}>
                    <option value={0}>Hepsi</option>
                    {Array.from(ExpertisePointGroupTypeLabel.entries()).map(([key, value]) => (
                      <option key={key} value={key}>
                        {value}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </StackItem>
              <StackItem w="300px">
                <ExpertisePointSelect
                  enableAll
                  groupType={selectedBrand === 0 ? undefined : selectedBrand}
                  onExpertisePointSelected={(selectedExpertisePoint) => {
                    setExpertisePointId(selectedExpertisePoint?.id);
                  }}
                />
              </StackItem>
            </>
          )}
        </HStack>

        <HStack align="center">
          <FormControl width={['100%', '100%', '25%']} marginBottom={55}>
            <FormLabel>
              <Trans>Tarih Aralığı</Trans>
            </FormLabel>
            <LocalizedRangeDatepicker selectedDates={selectedDates} onDateChange={setSelectedDates} />
          </FormControl>
        </HStack>

        {/* <Flex gap={12} direction="column" marginTop={55}> */}
        {transactionData?.transactions && (
          <AdminExpertisePointBalanceHistoryTable
            transactions={transactionData?.transactions}
            expertisePointId={expertisePointId}
            startDate={selectedDates[0].getTime()}
            endDate={selectedDates[1].getTime()}
          />
        )}
        {/* </Flex> */}
      </VStack>
    </>
  );
};
